import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// =============================================================================
// ResizeObserver Error Suppression (v3.0 - Comprehensive Fix)
// =============================================================================
// These are harmless warnings that occur when ResizeObserver callbacks take
// too long and don't affect functionality - they just clutter the console

// 1. Patch ResizeObserver to debounce and prevent errors
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      window.requestAnimationFrame(() => {
        try {
          callback(entries, observer);
        } catch (error) {
          // Silently catch ResizeObserver errors
          if (!error.message.includes('ResizeObserver loop')) {
            throw error; // Re-throw non-ResizeObserver errors
          }
        }
      });
    });
  }
};

// 2. Intercept at window error level (catches React dev overlay errors)
window.addEventListener('error', (event) => {
  if (
    event.message &&
    (event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
     event.message.includes('ResizeObserver loop limit exceeded'))
  ) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    return true;
  }
}, true); // Use capture phase to intercept early

// 3. Also handle unhandledrejection events
window.addEventListener('unhandledrejection', (event) => {
  if (
    event.reason &&
    event.reason.message &&
    (event.reason.message.includes('ResizeObserver loop') ||
     event.reason.message.includes('ResizeObserver loop limit'))
  ) {
    event.preventDefault();
    event.stopPropagation();
    return true;
  }
});

// 4. Override console.error to filter ResizeObserver warnings
const originalConsoleError = console.error;
console.error = function(...args) {
  // Check all arguments for ResizeObserver mentions
  const shouldSuppress = args.some(arg => {
    if (typeof arg === 'string') {
      return arg.includes('ResizeObserver loop') || 
             arg.includes('handleError') ||
             arg.includes('undelivered notifications');
    }
    if (arg instanceof Error) {
      return arg.message.includes('ResizeObserver loop') || 
             arg.message.includes('undelivered notifications');
    }
    if (arg && arg.toString) {
      const str = arg.toString();
      return str.includes('ResizeObserver loop') || 
             str.includes('undelivered notifications');
    }
    return false;
  });
  
  if (!shouldSuppress) {
    originalConsoleError.apply(console, args);
  }
};

// 5. Also suppress console.warn
const originalConsoleWarn = console.warn;
console.warn = function(...args) {
  const shouldSuppress = args.some(arg => {
    if (typeof arg === 'string') {
      return arg.includes('ResizeObserver loop');
    }
    if (arg instanceof Error) {
      return arg.message.includes('ResizeObserver loop');
    }
    return false;
  });
  
  if (!shouldSuppress) {
    originalConsoleWarn.apply(console, args);
  }
};

// =============================================================================
// App Initialization
// =============================================================================
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
